<template>
  <div class="drag-container">
    <ul class="drag-list">
      <li
        v-for="(stage, index) in stages"
        :key="stage.id"
        class="drag-column"
        :class="index === 0 ? 'drags' : {['drag-column-' + stage.id]: true}">
        <span class="drag-column-header">
          <slot :name="stage.id">
            <h2>
              {{ stage.name }}
              <d-badge
                v-if="stage.id.toString() === unassignedValue.toString()"
                :theme="unassignedCount > 0 ? 'primary' : 'success'"
                pill>
                {{ unassignedCount }}
              </d-badge>
            </h2>
          </slot>
        </span>
        <div class="drag-options" />
        <div
          ref="list"
          :key="itemsKey"
          class="drag-inner-list"
          :data-status="stage.id">
          <d-card
            v-for="block in getBlocks(stage.id)"
            v-if="source.length"
            :key="block[idProp]"
            class="drag-item"
            :data-block-id="block[idProp]">
            <slot :name="block[idProp]">
              <div class="align-middle">
                <span class="label-text">
                  <d-checkbox
                    v-model="toDrag"
                    inline
                    :value="block[idProp]"
                    class="m-0 align-middle" />

                  <span class="shop-name">{{ block.shop_name || ('Placówka o ID ' + block.shop_id) }}</span>
                </span>
              </div>
            </slot>
          </d-card>
        </div>
        <div class="drag-column-footer">
          <slot :name="`footer-${stage.id}`" />
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import dragula from 'dragula';
import { Machine } from 'xstate';

export default {
  name: 'drag-items',
  props: {
    autoSave: {
      default: true,
      type: Boolean
    },
    config: {
      default: () => ({}),
      type: Object
    },
    data: {
      required: true,
      type: Array
    },
    idProp: {
      default: 'id',
      type: String
    },
    refresh: {
      type: Function
    },
    source: {
      required: true,
      type: [Array, Object]
    },
    stages: {
      required: true,
      type: [Array, Object]
    },
    stateMachineConfig: {
      default: null,
      type: Object
    },
    statusProp: {
      default: 'supplier_id',
      type: String
    },
    suppliers: {
      required: true,
      type: Array
    },
    unassignedValue: {
      default: 0,
      type: [Number, String]
    }
  },
  data () {
    return {
      blocks: [],
      blocksNew: [],
      id: '',
      itemsKey: 0,
      machine: null,
      toDrag: []
    };
  },
  computed: {
    localBlocks () {
      return this.blocks;
    },
    unassignedCount () {
      return this.blocks.filter(block => String(block[this.statusProp]) === String(this.unassignedValue)).length;
    }
  },
  watch: {
    data: {
      handler (value) {
        if (value) {
          this.blocks = [];
          this.sortDataValue(value);
        }
      },
      immediate: true
    }
  },
  updated () {
    this.drake.containers = this.$refs.list;
    this.drake.mirrorContainer = this.$el;
  },
  mounted () {
    this.config.accepts = this.config.accepts || this.accepts;
    this.config.mirrorContainer = this.$el;
    this.toDrag = [];
    this.drake = dragula(this.$refs.list, this.config)
      .on('drag', (el, source) => {
        this.$emit('drag', el, source);
        el.classList.add('is-moving');
        this.allowedTargets(el, source).forEach(c => c.classList.add('allowed'));
        this.forbiddenTargets(el, source).forEach(c => c.classList.add('forbidden'));
      })
      .on('dragend', (el) => {
        this.$emit('dragend', el);
        el.classList.remove('is-moving');
        this.drake.containers.forEach(c => c.classList.remove('allowed', 'forbidden'));
        window.setTimeout(() => {
          el.classList.add('is-moved');
          window.setTimeout(() => {
            el.classList.remove('is-moved');
          }, 600);
        }, 100);
      })
      .on('drop', (block, list, source, sibling) => {
        this.$emit('drop', block, list, source, sibling);

        let index = 0;
        let newOrder = [];
        let newState = list.dataset.status;

        if (this.machine) {
          const transition = this.findTransition(list, source);
          if (!transition) {
            return;
          }
          newState = this.machine.transition(source.dataset.status, transition).value;
        }

        for (index = 0; index < list.children.length; index += 1) {
          newOrder.push(list.children[index].dataset.blockId);
        }

        const el = this.toDrag.indexOf(Number(block.dataset.blockId) || block.dataset.blockId);

        if (el > -1) {
          this.toDrag.splice(el, 1);
        }

        if (this.toDrag.length > 0) {
          const newItemIndex = newOrder.indexOf(block.dataset.blockId);

          if (newItemIndex === 0) {
            newOrder = [block.dataset.blockId, ...this.toDrag, ...newOrder.slice(newItemIndex, newOrder.length)];
          } else if (newItemIndex === newOrder.length - 1) {
            newOrder = [...newOrder, ...this.toDrag];
          } else {
            newOrder = [...newOrder.slice(0, newItemIndex + 1), ...this.toDrag, ...newOrder.slice(newItemIndex + 1, newOrder.length)];
          }
        }

        this.$forceUpdate();
        this.$emit('update-block', block.dataset.blockId, newState, index);
        this.sortData(this.drake.containers, block.dataset.blockId, newState, index, newOrder);
      })
      .on('cancel', (el, container, source) => {
        this.$emit('cancel', el, container, source);
      })
      .on('remove', (el, container, source) => {
        this.$emit('remove', el, container, source);
      })
      .on('shadow', (el, container, source) => {
        this.$emit('shadow', el, container, source);
      })
      .on('over', (el, container, source) => {
        this.$emit('over', el, container, source);
      })
      .on('out', (el, container, source) => {
        this.$emit('out', el, container, source);
      })
      .on('cloned', (clone, original, type) => {
        this.$emit('cloned', clone, original, type);
    });
    this.$emit('init', this.drake);
  },
  created () {
    if (!this.stateMachineConfig) {
      return;
    }
    this.machine = Machine(this.stateMachineConfig);
  },
  methods: {
    sortData (block, ItemId, status, index, newOrder) {
      const items = JSON.parse(JSON.stringify(this.blocks));
      let id = ItemId.toString();
      newOrder = newOrder.map(item => item.toString());

      for (let i = 0; i < items.length; i++) {
        if (items[i][this.idProp].toString() === id || this.toDrag.includes(items[i][this.idProp])) {
          let elementID = items[i][this.idProp];

          items[i].index = newOrder.indexOf(elementID.toString()) >= 0
            ? newOrder.indexOf(elementID.toString())
            : index;

          items[i][this.statusProp] = /^\d+$/.test(String(status)) ? Number(status) : status;
        } else {
          let elementID = items[i][this.idProp];

          let newPos = newOrder.indexOf(elementID.toString());
          let fallbackPos = items[i].index;

          if (fallbackPos === undefined) {
            fallbackPos = items[i].ordering;
          }

          items[i].index = newPos >= 0 ? newPos : fallbackPos;
        }
      }

      this.blocks = [];
      this.toDrag = [];
      this.itemsKey += 1;

      this.blocksNew = items;
      this.sortDataValue(items);
      this.submitScheduleStore();
    },
    submitScheduleStore () {
      if (!this.autoSave) {
        return;
      }

      let formDataToSend = this.prepareFormDataToSend();
      this.$http.post('/orders/update-ordering', formDataToSend).then(() => {
        if (this.refresh) {
          this.refresh();
        }
      }).catch((error) => {
        console.error(error);
      });
    },
    prepareFormDataToSend () {
      return {
        items: this.blocksNew
      };
    },
    sortDataValue (data) {
      const items = JSON.parse(JSON.stringify(data));

      function compare (a, b) {
        let orderA = a.index !== undefined ? a.index : a.ordering;
        let orderB = b.index !== undefined ? b.index : b.ordering;

        if (orderA < orderB) {
          return -1;
        }
        if (orderA > orderB) {
          return 1;
        }
        return 0;
      }

      items.sort(compare);
      this.blocks = items;
      this.blocksNew = items;
    },
    getBlocks (status) {
      return this.localBlocks.filter(block => String(block[this.statusProp]) === String(status));
    },
    findPossibleTransitions (sourceState) {
      return this.machine.config.states[sourceState].on || {};
    },
    findTransition (target, source) {
      const targetState = target.dataset.status;
      const sourceState = source.dataset.status;
      const possibleTransitions = this.findPossibleTransitions(sourceState);
      return Object.keys(possibleTransitions)
        .find(transition => possibleTransitions[transition] === targetState);
    },
    accepts (block, target, source) {
      if (!this.machine) {
        return true;
      }
      const targetState = target.dataset.status;
      const sourceState = source.dataset.status;
      return Object.values(this.findPossibleTransitions(sourceState)).includes(targetState);
    },
    allowedTargets (el, source) {
      const block = this.localBlocks.find(b => b[this.idProp] === el.dataset.blockId);
      return this.drake.containers.filter(c => this.config.accepts(block, c, source));
    },
    forbiddenTargets (el, source) {
      return this.drake.containers.filter(c => !this.allowedTargets(el, source).includes(c));
    }
  }
};
</script>

<style lang="scss">
$ease-out: all .3s cubic-bezier(0.23, 1, 0.32, 1);

.drag-container {
  margin: 0.5rem auto;
  padding: 0;
  width: 100%;
}

.drag-list {
  align-items: flex-start;
  display: flex;
  list-style-type: none;
  margin: 0;
  max-width: 100%;
  min-height: calc(100vh - 420px);
  overflow: auto;
  padding-inline-start: 0.35rem;
}

.drag-column {
  background: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0 0.125rem 0.25rem rgba(90, 97, 105, 0.08);
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 5px 10px;
  max-width: 280px;
  min-width: 230px;
  position: relative;

  &-header {
    align-items: center;
    background: #f8f9fa;
    border-bottom: 2px solid transparent;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    display: flex;
    justify-content: space-between;
    padding: 15px;
    position: sticky;
    top: 0;
    z-index: 10;

    h2 {
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2em;
      margin: 0;
      padding: 0 32px;
      position: relative;
      text-align: center;
      text-transform: uppercase;
      width: 100%;

      .badge {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  &.drags {
    background: #e9ecef;
    border: 1px solid #dee2e6;
    box-shadow: 3px 0 6px rgba(0, 0, 0, 0.06);
    left: 0;
    position: sticky;
    z-index: 20;

    .drag-column-header {
      background: #e9ecef;
      border-bottom: 2px solid #ced4da;
      z-index: 21;
    }
  }

  &:not(.drags) {
    background: #ffffff;
    border: 1px solid #e1e5eb;

    .drag-column-header {
      background: #ffffff;
      border-bottom: 2px solid #17c671;
    }

    .drag-inner-list {
      &:empty {
        border-color: #cbd5e1;
        justify-content: center;

        &::after {
          color: #94a3b8;
          content: 'Przeciągnij placówki tutaj, aby zaplanować trasę';
          font-size: 0.85rem;
          font-weight: 400;
          line-height: 1.4;
          padding: 0 10px;
          text-align: center;
        }
      }
    }
  }
}

.drag-inner-list {
  border: 2px dashed transparent;
  border-radius: 8px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  list-style-type: none;
  margin: 8px 3px;
  min-height: 150px;
  padding: 5px 3px;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &.allowed {
    background-color: #f0fdf4 !important;
    border-color: #4ade80 !important;
  }
}

.drag-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: grab;
  margin: 0;
  padding: 8px;
  transition: $ease-out;

  .shop-name {
    position: relative;
    top: 1px;
  }

  &:active {
    cursor: grabbing;
  }

  &.is-moving {
    background: #ffffff;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
    z-index: 9999;
  }
}

.label-text {
  color: var(--table-color);
  display: block;
  width: 100%;
}

.gu-mirror {
  list-style-type: none;
  margin: 0 !important;
  opacity: 0.9;
  position: fixed !important;
  z-index: 9999 !important;
}

.gu-hide {
  display: none !important;
}

.gu-unselectable {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}

.gu-transit {
  opacity: 0.2;
}
</style>
